import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import MuxVideoUpload from './MuxVideoUpload';
import MuxVideoPlayer from './MuxVideoPlayer';
import { Trash2, Film, Plus } from 'lucide-react';

const LevelFourSectionEdit = ({ data, onChange, errors, songId, videoUrl = '', onVideoUrlChange, handleSaveLevel, handleCancelLevel, levelSaving, levelSaved, hasUnsavedChanges }) => {
  const [exerciseUploadId, setExerciseUploadId] = useState(null); // exercise id currently showing upload modal

  // Initialize default structure for Level 4 with Performance and Exercises subsections
  const initializeDefaultData = () => {
    return {
      performance: {
        arc: '',
        physicality: '',
        facialExpression: '',
        connection: ''
      },
      exercises: []
    };
  };

  // Get current data or initialize default
  const getCurrentData = () => {
    if (!data || typeof data !== 'object') {
      return initializeDefaultData();
    }

    return {
      performance: {
        arc: data?.performance?.arc || data?.arc || '',
        physicality: data?.performance?.physicality || data?.physicality || '',
        facialExpression: data?.performance?.facialExpression || data?.facialExpression || '',
        connection: data?.performance?.connection || data?.connection || ''
      },
      exercises: Array.isArray(data?.exercises) ? data?.exercises : []
    };
  };

  const currentData = getCurrentData();

  // Handle performance field changes
  const handlePerformanceChange = (field, value) => {
    const updatedData = {
      ...currentData,
      performance: {
        ...currentData?.performance,
        [field]: value
      }
    };
    onChange(updatedData);
  };

  // Handle exercise changes
  const addExercise = (e) => {
    // Prevent form submission
    e?.preventDefault();
    e?.stopPropagation();

    const newExercise = {
      id: Date.now(),
      header: '',
      content: '',
      video: null // { id, mux_playback_id, title, thumbnail_url, status }
    };
    
    const updatedData = {
      ...currentData,
      exercises: [...currentData?.exercises, newExercise]
    };
    onChange(updatedData);
  };

  const updateExercise = (exerciseId, field, value) => {
    const updatedExercises = currentData?.exercises?.map(exercise => 
      exercise?.id === exerciseId 
        ? { ...exercise, [field]: value }
        : exercise
    );
    
    const updatedData = {
      ...currentData,
      exercises: updatedExercises
    };
    onChange(updatedData);
  };

  const removeExercise = (exerciseId, e) => {
    // Prevent form submission
    e?.preventDefault();
    e?.stopPropagation();

    const filteredExercises = currentData?.exercises?.filter(exercise => exercise?.id !== exerciseId);
    
    const updatedData = {
      ...currentData,
      exercises: filteredExercises
    };
    onChange(updatedData);
  };

  const handleExerciseVideoReady = (exerciseId, videoData) => {
    updateExercise(exerciseId, 'video', videoData);
    setExerciseUploadId(null);
  };

  const removeExerciseVideo = (exerciseId, e) => {
    e?.preventDefault();
    e?.stopPropagation();
    updateExercise(exerciseId, 'video', null);
  };

  return (
    <div className="space-y-8">
      {/* Mux Video URL */}
      <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
        <h3 className="text-xl font-heading font-semibold text-white mb-4 flex items-center space-x-2">
          <Icon name="Film" size={24} className="text-blue-200" />
          <span>Level 4 Video</span>
          {hasUnsavedChanges && (
            <span className="inline-flex items-center space-x-1 bg-amber-500 bg-opacity-20 border border-amber-400 border-opacity-50 text-amber-300 text-xs px-2 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" />
              <span>Unsaved</span>
            </span>
          )}
        </h3>
        <div>
          <label className="block text-sm font-medium text-blue-100 mb-2">
            Mux Playback ID
          </label>
          <input
            type="text"
            value={videoUrl}
            onChange={(e) => onVideoUrlChange?.(e?.target?.value)}
            className="w-full px-4 py-2 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="e.g. DS00Spx1CV902MCtPj5WknGlR017IEmVd"
          />
          <p className="text-blue-300 text-xs mt-1">
            Enter the Mux playback_id from your Mux dashboard or the videos table. Leave blank to hide the video for this level.
          </p>
        </div>
      </div>

      {/* Performance Subsection */}
      <div className="border border-white border-opacity-20 rounded-xl p-6 bg-white bg-opacity-5">
        <div className="flex items-center justify-between mb-6">
          <h4 className="text-lg font-semibold text-white flex items-center space-x-2">
            <Icon name="Zap" size={20} className="text-blue-300" />
            <span>Performance</span>
          </h4>
        </div>

        {/* Four Performance Headers with text boxes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-blue-100 mb-2">
              Arc
            </label>
            <textarea
              value={currentData?.performance?.arc || ''}
              onChange={(e) => handlePerformanceChange('arc', e?.target?.value)}
              className="w-full px-4 py-3 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Enter arc analysis..."
              rows={4}
              style={{ whiteSpace: 'pre-wrap' }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-blue-100 mb-2">
              Physicality
            </label>
            <textarea
              value={currentData?.performance?.physicality || ''}
              onChange={(e) => handlePerformanceChange('physicality', e?.target?.value)}
              className="w-full px-4 py-3 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Enter physicality notes..."
              rows={4}
              style={{ whiteSpace: 'pre-wrap' }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-blue-100 mb-2">
              Facial Expression
            </label>
            <textarea
              value={currentData?.performance?.facialExpression || ''}
              onChange={(e) => handlePerformanceChange('facialExpression', e?.target?.value)}
              className="w-full px-4 py-3 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Enter facial expression guidance..."
              rows={4}
              style={{ whiteSpace: 'pre-wrap' }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-blue-100 mb-2">
              Connection
            </label>
            <textarea
              value={currentData?.performance?.connection || ''}
              onChange={(e) => handlePerformanceChange('connection', e?.target?.value)}
              className="w-full px-4 py-3 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Enter connection notes..."
              rows={4}
              style={{ whiteSpace: 'pre-wrap' }}
            />
          </div>
        </div>
      </div>

      {/* Exercises Subsection */}
      <div className="border border-white border-opacity-20 rounded-xl p-6 bg-white bg-opacity-5">
        <div className="flex items-center justify-between mb-6">
          <h4 className="text-lg font-semibold text-white flex items-center space-x-2">
            <Icon name="Dumbbell" size={20} className="text-blue-300" />
            <span>Exercises</span>
          </h4>
          
          {/* Add Exercise Button - Fixed to prevent form submission */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addExercise}
            className="border-blue-400 text-blue-200 hover:bg-blue-500 hover:bg-opacity-20 rounded-lg"
            iconName="Plus"
            iconPosition="left"
          >
            Add Exercise
          </Button>
        </div>

        <div className="space-y-6">
          {currentData?.exercises?.length === 0 ? (
            <div className="text-center py-8 text-blue-200">
              <Icon name="Dumbbell" size={48} className="mx-auto mb-4 opacity-50" />
              <p>No exercises added yet. Add your first exercise above.</p>
            </div>
          ) : (
            currentData?.exercises?.map((exercise, index) => (
              <div key={exercise?.id} className="border border-white border-opacity-10 rounded-lg p-4 bg-white bg-opacity-5">
                {/* Exercise Header */}
                <div className="flex items-center justify-between mb-4">
                  <h5 className="text-md font-medium text-white">Exercise {index + 1}</h5>
                  <div className="flex items-center space-x-2">
                    {/* Mux Add Video button */}
                    {!exercise?.video ? (
                      <button
                        type="button"
                        onClick={(e) => { e?.preventDefault(); e?.stopPropagation(); setExerciseUploadId(exercise?.id); }}
                        className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium px-2.5 py-1.5 rounded-lg transition-colors"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        Add Video
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => removeExerciseVideo(exercise?.id, e)}
                        className="flex items-center gap-1.5 text-red-300 hover:text-red-200 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-red-400 border-opacity-40 hover:bg-red-500 hover:bg-opacity-20 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Remove Video
                      </button>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={(e) => removeExercise(exercise?.id, e)}
                      className="text-red-300 hover:bg-red-500 hover:bg-opacity-20 rounded-lg"
                      iconName="Trash2"
                      iconPosition="left"
                    >
                      Remove
                    </Button>
                  </div>
                </div>

                {/* Exercise Video Player */}
                {exercise?.video && (
                  <div className="mb-4 rounded-xl overflow-hidden">
                    {exercise?.video?.status === 'ready' && exercise?.video?.mux_playback_id ? (
                      <MuxVideoPlayer
                        videoId={exercise?.video?.id}
                        title={exercise?.video?.title}
                        thumbnailUrl={exercise?.video?.thumbnail_url || ''}
                        captions={exercise?.video?.captions || []}
                        onVideoComplete={() => {}}
                        onWatchEvent={() => {}}
                      />
                    ) : (
                      <div className="aspect-video bg-white bg-opacity-10 flex items-center justify-center rounded-xl">
                        <div className="text-center">
                          <Film className="w-8 h-8 text-blue-300 mx-auto mb-2" />
                          <p className="text-sm text-blue-200">
                            {exercise?.video?.status === 'waiting' || exercise?.video?.status === 'preparing' ?'Processing video…'
                              : `Status: ${exercise?.video?.status}`}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Exercise Header Field */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-blue-100 mb-2">
                    Exercise Header
                  </label>
                  <input
                    type="text"
                    value={exercise?.header || ''}
                    onChange={(e) => updateExercise(exercise?.id, 'header', e?.target?.value)}
                    className="w-full px-4 py-3 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    placeholder="Enter exercise header..."
                  />
                </div>

                {/* Exercise Content Field */}
                <div>
                  <label className="block text-sm font-medium text-blue-100 mb-2">
                    Exercise Content
                  </label>
                  <textarea
                    value={exercise?.content || ''}
                    onChange={(e) => updateExercise(exercise?.id, 'content', e?.target?.value)}
                    className="w-full px-4 py-3 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    placeholder="Enter exercise instructions and details..."
                    rows={4}
                    style={{ whiteSpace: 'pre-wrap' }}
                  />
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {errors?.level4 && (
        <p className="text-red-300 text-sm mt-1">{errors?.level4}</p>
      )}

      {/* Mux Upload Modal for exercise video */}
      {exerciseUploadId !== null && (
        <MuxVideoUpload
          onVideoReady={(videoData) => handleExerciseVideoReady(exerciseUploadId, videoData)}
          onClose={() => setExerciseUploadId(null)}
          songId={songId}
          level="level4"
        />
      )}

      {/* Per-Level Save / Cancel */}
      <div className="flex justify-end items-center space-x-3 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => handleCancelLevel?.('level4')}
          className="border-white border-opacity-40 text-white hover:bg-white hover:bg-opacity-10"
        >
          Cancel
        </Button>
        <Button
          type="button"
          disabled={levelSaving?.level4}
          onClick={() => handleSaveLevel?.('level4')}
          iconName={levelSaved?.level4 ? 'Check' : 'Save'}
          iconPosition="left"
          className={`bg-white text-indigo-900 hover:bg-gray-100 ${levelSaving?.level4 ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {levelSaving?.level4 ? 'Saving...' : levelSaved?.level4 ? 'Saved!' : 'Save Level 4'}
        </Button>
      </div>
    </div>
  );
};

export default LevelFourSectionEdit;